import { Dispatch, useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Field, useFormState } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataErpId,
  getKnmDataResigningRequired,
  getKnmFormRef,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsNew,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IKnmPmFormRequirementsNpa } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormRequirementsNpa';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { PM_ACT } from 'CorePackage/MainModule/routes/routes';
import { getIsDirtyAct } from 'KnmPackage/KnmPaperlessActModule/KnmActSignButton/getIsDirtyAct';
import { getHasModifiedDirtyFields } from 'KnmPackage/MainModule/common/getHasModifiedDirtyFields';
import { knmSetIsNeedActSaveOrSign } from 'KnmPackage/MainModule/actions/knmActions';
import { IKnmPmFormAct } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormAct';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { HadToBeHowInSignMessage } from 'KnmErknmPackage/MainModule/components/HadToBeHowInSignMessage';
import {
  ADD_ACT_ERROR_BY_EMPTY_SUBJECT_NOTIFICATION_PM,
  ADD_ACT_ERROR_BY_NOT_FILLED_SUSPENSION,
} from 'CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { useStableObject } from 'hooks/useStableObject';
import { createTypedUseFormValue } from 'hooks/useTypedFormField';

import { PmActItem } from './PmActItem/PmActItem';
import { IKnmErknmFormObject } from '../../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { IKnmFormRiskIndicators } from '../../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';

interface IPmActProps {
  watchOnly: boolean;
  values: IKnmPmForm;
  requirementsNpa: IKnmPmFormRequirementsNpa[] | undefined;
  knoOrganization: IDictionaryKnoOrganizationSelectOption | undefined;
  supervisionType: IDictionarySupervisionTypeSelectOption | undefined;
  objectsPm?: IKnmErknmFormObject[];
  riskIndicators?: IKnmFormRiskIndicators[];
}

interface IPmActStoreProps {
  resigningRequired: boolean;
  formRef: IKnmFormRef | undefined;
  erpId: string | undefined;
  isKnmNew: boolean;
  isErknmStatusTypeWaitingCarryOut: boolean;
  isErknmStatusTypeCarriedOut: boolean;
  isErknmStatusTypeComplete: boolean;
}

const useValue = createTypedUseFormValue<IKnmPmForm>();

function PmActWithoutStore({
  formRef,
  watchOnly,
  resigningRequired,
  values,
  requirementsNpa,
  knoOrganization,
  objectsPm,
  riskIndicators,
  supervisionType,
  erpId,
  isKnmNew,
  isErknmStatusTypeWaitingCarryOut,
  isErknmStatusTypeCarriedOut,
  isErknmStatusTypeComplete,
}: IPmActProps & IPmActStoreProps) {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const act = useValue('act');

  const isDisableAct = watchOnly;
  const name = 'act';

  /**
   * Обработчик кнопки "Добавить"
   */
  const addAct = useCallback(() => {
    if (!isDisableAct || !resigningRequired) {
      const subjectNotifications = formRef?.current.getFieldState('subjectNotifications')?.value;
      const suspensionEvents = formRef?.current.getFieldState('suspensionEvents')?.value;

      if (suspensionEvents?.find(se => se.isNew)) {
        return dispatch(
          addNoticeAction({
            renderContent: ADD_ACT_ERROR_BY_NOT_FILLED_SUSPENSION,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ACT_ERRORS,
          }),
        );
      }

      if (!subjectNotifications || !subjectNotifications.length) {
        dispatch(
          addNoticeAction({
            renderContent: ADD_ACT_ERROR_BY_EMPTY_SUBJECT_NOTIFICATION_PM,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ACT_ERRORS,
          }),
        );

        return;
      }

      const uuid = uuidv4();

      formRef?.current.change('act', {
        guid: uuid,
        isNew: true,
        extractVersion: undefined,
        involvedWitnesses: undefined,
        involvedExperts: undefined,
        acquaintanceLackNoInfoComment: undefined,
        acquaintanceLackType: undefined,
        acquaintanceType: undefined,
        canceledReason: undefined,
        canceledReasonDescription: undefined,
        created: undefined,
        createdBy: undefined,
        creationActDateTime: undefined,
        document: undefined,
        durationDays: undefined,
        durationHours: undefined,
        events: undefined,
        fioAndPositionSinger: undefined,
        fioReader: undefined,
        fioSigner: undefined,
        holdingDateTime: undefined,
        isViolatedMandatoryRequirements: undefined,
        isViolationResolved: undefined,
        isViolationResolvedNote: undefined,
        knoInspectors: undefined,
        listId: uuid,
        nextWordDayActDateTime: undefined,
        noteInvalidate: undefined,
        numberAct: `${erpId}-1`,
        organization: undefined,
        refuserFio: undefined,
        refuserTitle: undefined,
        canceled: false,
        titleReader: undefined,
        titleSigner: undefined,
        typeAct: undefined,
        updated: undefined,
        updatedBy: undefined,
        violatedMandatoryRequirements: undefined,
        familiarizationNotifications: undefined,
        remarks: undefined,
        signed: undefined,
        signActDateTime: undefined,
        paperless: true,
        firstExtractDateTime: null,
        systemSignatureUsed: false,
        systemSignatureMnemonic: null,
        systemSignatureDateTime: null,
        systemSignatureTitle: null,
        signers: undefined,
      });
    }
  }, [isDisableAct, formRef, erpId, resigningRequired]);

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.PM_PROF_ACT_TYPES));
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_CANCELED_REASONS));
  }, [dispatch]);

  /**
   * Обработчик удаления акта
   */
  const onRemoveAct = useCallback(() => {
    if (!isDisableAct && !resigningRequired) {
      formRef?.current.change(name as keyof IKnmForm);
    }
  }, [isDisableAct, formRef, resigningRequired]);

  const { dirtyFields: currentDirtyFieldsUnsable, dirty: isDirty, values: currentValues } = useFormState({
    subscription: { dirtyFields: true, dirty: true, values: true },
  });

  const currentDirtyFields = useStableObject(currentDirtyFieldsUnsable);

  /**
   * Расчет блокировки кнопки "Добавить" при наличии изменений в паспорте (за исключением акта)
   */
  const isDisabledByDirty = useMemo(() => {
    if (isErknmStatusTypeCarriedOut || isErknmStatusTypeWaitingCarryOut || isErknmStatusTypeComplete) {
      const hasDirtyAct = getIsDirtyAct(currentDirtyFields, 0, true, false, formRef).wasEditFiled;

      // Исключение полей акта для проверки на modified
      const formDirtyFieldsWithoutActs = Object.entries(currentDirtyFields).reduce((acc, value) => {
        return !value[0].includes('act') ? { ...acc, [value[0]]: value[1] } : acc;
      }, {});

      const hasModifiedDirtyFields = getHasModifiedDirtyFields(formDirtyFieldsWithoutActs, formRef);

      return !hasDirtyAct && isDirty && hasModifiedDirtyFields;
    }

    return false;
  }, [
    formRef,
    isErknmStatusTypeCarriedOut,
    isErknmStatusTypeWaitingCarryOut,
    isErknmStatusTypeComplete,
    currentDirtyFields,
    isDirty,
    currentValues,
  ]);

  /**
   * Проверка наличия необходимости сохранения или подписания одного из актов
   */
  useEffect(() => {
    if (isErknmStatusTypeCarriedOut || isErknmStatusTypeWaitingCarryOut || isErknmStatusTypeComplete) {
      if (act) {
        const getIsNeedActSaveOrSign = (currentAct: IKnmPmFormAct) => {
          if (!currentAct.isNew) {
            const { wasEditFiled: isDirtyBySigned } = getIsDirtyAct(currentDirtyFields, 0, true, false, formRef);

            // Алгоритм аналогичный KnmActSignButton
            if (currentAct.signed) {
              if (isDirtyBySigned) {
                return true;
              }

              return false;
            }
            if (currentAct.isNew || isDirty) {
              return true;
            }

            return true;
          }

          return true;
        };

        const isNeedActSaveOrSign = getIsNeedActSaveOrSign(act);

        dispatch(knmSetIsNeedActSaveOrSign(isNeedActSaveOrSign));
      } else {
        dispatch(knmSetIsNeedActSaveOrSign(false));
      }
    }

    return () => {
      dispatch(knmSetIsNeedActSaveOrSign(false));
    };
  }, [
    act,
    isDirty,
    isErknmStatusTypeCarriedOut,
    isErknmStatusTypeWaitingCarryOut,
    isErknmStatusTypeComplete,
    currentDirtyFields,
  ]);

  const helpHit = useMemo(() => {
    if (!isErknmStatusTypeCarriedOut && !isErknmStatusTypeWaitingCarryOut && !isErknmStatusTypeComplete) {
      return undefined;
    }
    if (act && !act.isNew && !act.signed) {
      return <HadToBeHowInSignMessage message={'Необходимо переподписать изменения в акте'} />;
    }
    if (isDisabledByDirty || resigningRequired) {
      return <HadToBeHowInSignMessage message={'Необходимо сохранить и подписать изменения в паспорте'} />;
    }

    return undefined;
  }, [
    act,
    isDisabledByDirty,
    resigningRequired,
    isErknmStatusTypeCarriedOut,
    isErknmStatusTypeWaitingCarryOut,
    isErknmStatusTypeComplete,
  ]);

  return (
    <Field name={name}>
      {({ input }) => {
        return (
          <>
            <Section
              {...input}
              title={'Сведения об акте'}
              id={PM_ACT}
              type="list"
              customButton={
                <Button
                  className="shared-btn-add"
                  text="Добавить"
                  type="button"
                  variant="link"
                  color="primary"
                  disabled={
                    isDisableAct ||
                    resigningRequired ||
                    !!act ||
                    isKnmNew ||
                    !isErknmStatusTypeWaitingCarryOut ||
                    isDisabledByDirty
                  }
                  onClick={addAct}
                />
              }
            >
              <div>{helpHit}</div>
              {act && (
                <PmActItem
                  requirementsNpa={requirementsNpa}
                  value={act}
                  name={name}
                  isWatchOnly={isDisableAct}
                  isDisabledByDirty={isDisabledByDirty}
                  onRemove={onRemoveAct}
                  values={values}
                  knoOrganization={knoOrganization}
                  supervisionType={supervisionType}
                  objectsPm={objectsPm}
                  riskIndicators={riskIndicators}
                />
              )}
            </Section>
          </>
        );
      }}
    </Field>
  );
}

const mapStateToProps = (state: IRootState): IPmActStoreProps => ({
  resigningRequired: getKnmDataResigningRequired(state),
  formRef: getKnmFormRef(state),
  erpId: getKnmDataErpId(state),
  isKnmNew: getKnmIsNew(state),
  isErknmStatusTypeComplete: getKnmIsErknmStatusTypeCompleted(state),
  isErknmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isErknmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
});

export const PmAct = connect(mapStateToProps)(PmActWithoutStore);
